// src/components/cooler/inventory/CoolerInventoryManager.tsx

import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  TextField,
  Chip,
  Typography,
  IconButton,
  InputAdornment,
  Button,
  Stack,
  Alert,
  Snackbar,
  CircularProgress,
} from '@mui/material';
import {
  Search,
  Upload as UploadIcon,
  Download,
  Settings,
  ArrowLeft,
  Plus,
  Filter,
  Grid3X3,
  List,
} from 'lucide-react';
import axiosInstance from '../../../utils/axiosConfig';

import {
  CATEGORIES,
  Category,
  LOCATIONS,
  SECOND_LOCATIONS,
  shouldShowSerialNumber
} from '../constants/inventoryConstants';
import type { 
  InventoryItem,
  FilterOptions,
  MaintenanceRecord,
  NewInventoryItem,
  InventoryUpdateData,
  ImageReference,
  FilterChangeHandler
} from '../types/inventoryTypes';

import { FilterDrawer } from './FilterDrawer';
import { ItemGrid } from './ItemGrid';
import { AddItemDialog } from './AddItemDialog';
import { MaintenanceDialog } from './MaintenanceDialog';
import { ItemDetailsDialog } from './components/ItemDetails';
import { ItemTable } from './ItemTable';

// Debounce helper - OUTSIDE COMPONENT
const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const CoolerInventoryManager: React.FC = () => {
  // Existing state
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState<FilterOptions>({});
  const [inventory, setInventory] = useState<InventoryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [orphanCount, setOrphanCount] = useState(0);

  // Dialog states
  const [addItemDialogOpen, setAddItemDialogOpen] = useState(false);
  const [maintenanceDialogOpen, setMaintenanceDialogOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');

  // Selected item state
  const [selectedItem, setSelectedItem] = useState<InventoryItem | null>(null);

  // Notification state
  const [notification, setNotification] = useState<{
    message: string;
    type: 'success' | 'error';
    open: boolean;
  }>({
    message: '',
    type: 'success',
    open: false,
  });

  // UTILITY FUNCTIONS FIRST (before they're used)
  const showNotification = useCallback((message: string, type: 'success' | 'error') => {
    setNotification({
      message,
      type,
      open: true,
    });
  }, []);

  const handleFilterChange: FilterChangeHandler = useCallback((newFilters) => {
    console.log('Filter change received:', newFilters);
    
    setFilters(prev => {
      // If newFilters is empty object, reset ALL filters completely
      if (Object.keys(newFilters).length === 0) {
        console.log('Resetting all filters to empty state');
        return {};
      }

      const updatedFilters = { ...prev } as FilterOptions;

      // Handle each filter type separately to ensure type safety
      Object.entries(newFilters).forEach(([key, value]) => {
        if (value === undefined || 
            value === null || 
            value === '' ||
            (Array.isArray(value) && value.length === 0)) {
          // Remove the filter completely
          delete updatedFilters[key as keyof FilterOptions];
          console.log(`Removing filter: ${key}`);
        } else if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
          // Handle object filters like date ranges and value ranges with type safety
          const obj = value as any;
          if ((obj.start === '' && obj.end === '') || (obj.min === 0 && obj.max === 10000)) {
            // Remove empty/default ranges
            delete updatedFilters[key as keyof FilterOptions];
            console.log(`Removing empty range filter: ${key}`);
          } else {
            // Set the filter
            (updatedFilters as any)[key] = value;
            console.log(`Setting object filter: ${key} =`, value);
          }
        } else {
          // Set the filter
          (updatedFilters as any)[key] = value;
          console.log(`Setting filter: ${key} =`, value);
        }
      });

      console.log('Updated filters state:', updatedFilters);
      return updatedFilters;
    });
  }, []);

  // FETCH INVENTORY FUNCTION
  const fetchInventory = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      console.log('Current filters:', filters);
  
      // Basic search
      if (searchTerm) params.append('search', searchTerm);
      
      // Category filters
      if (filters.category?.length) {
        params.append('categories', filters.category.join(','));
      }
      
      // Location filters
      if (filters.location?.length) {
        params.append('location', filters.location.join(','));
      }
      
      if (filters.secondLocation) {
        params.append('secondLocation', filters.secondLocation);
      }
  
      // Boolean filters
      if (filters.requiresPower !== undefined) {
        params.append('requiresPower', filters.requiresPower.toString());
      }
      if (filters.isInsured !== undefined) {
        params.append('isInsured', filters.isInsured.toString());
      }
  
      // Array filters
      if (filters.maintenanceStatus?.length) {
        params.append('maintenanceStatus', filters.maintenanceStatus.join(','));
      }
      if (filters.vendor?.length) {
        params.append('vendors', filters.vendor.join(','));
      }
      if (filters.tags?.length) {
        params.append('tags', filters.tags.join(','));
      }
  
      // Special filters
      if (filters.hasDocuments !== undefined) {
        params.append('hasDocuments', filters.hasDocuments.toString());
      }
      if (filters.hasRelatedEquipment !== undefined) {
        params.append('hasRelatedEquipment', filters.hasRelatedEquipment.toString());
      }
  
      // Sort params
      if (filters.sortBy) params.append('sortBy', filters.sortBy);
      if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);
  
      console.log('Params before API call:', params.toString());
  
      const response = await axiosInstance.get(`/api/inventory?${params.toString()}`);
      
      // Client-side filtering
      let filteredData = response.data;
      filteredData = filteredData.filter((item: InventoryItem) => {
        const locationMatch = !filters.location?.length || 
          filters.location.includes(item.primaryLocation);
        const shelfMatch = !filters.secondLocation || 
          item.secondLocation === filters.secondLocation;
        return locationMatch && shelfMatch;
      });
  
      setInventory(filteredData);
    } catch (error) {
      console.error('Failed to fetch inventory:', error);
      setError('Failed to load inventory items');
    } finally {
      setLoading(false);
    }
  };

  // ORPHAN FILE FUNCTIONS
  const checkOrphanedFiles = async () => {
    try {
      console.log('🔍 Checking orphaned files...');
      const response = await axiosInstance.get('/api/Inventory/storage-stats');
      console.log('📊 Storage stats response:', response.data);
      setOrphanCount(response.data.cloudStorage.orphanedFiles);
    } catch (error) {
      console.error('Failed to check orphaned files:', error);
      if (typeof error === 'object' && error !== null && 'response' in error) {
        // @ts-expect-error: error is unknown, but we expect response for axios errors
        console.error('Error details:', error.response?.data);
      }
    }
  };

  const handleCleanupOrphans = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/api/Inventory/cleanup-orphaned-files?dryRun=false&olderThanHours=24');
      showNotification(`Cleaned up ${response.data.deletedCount} orphaned files`, 'success');
      checkOrphanedFiles(); // Refresh count
    } catch (error) {
      console.error('Cleanup failed:', error);
      showNotification('Cleanup failed', 'error');
    } finally {
      setLoading(false);
    }
  };

  // ALL THE useEffect HOOKS
  useEffect(() => {
    checkOrphanedFiles();
  }, []);

  useEffect(() => {
    fetchInventory();
  }, [filters, searchTerm]);

  // KEYBOARD SHORTCUT FOR ESCAPE KEY
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Only clear filters if Escape is pressed and no modals are open
      if (event.key === 'Escape' && 
          !addItemDialogOpen && 
          !maintenanceDialogOpen && 
          !detailsDialogOpen && 
          !filterDrawerOpen) {
        
        // Only clear if there are active filters to clear
        if (Object.keys(filters).length > 0) {
          handleFilterChange({});
          showNotification('Filters cleared', 'success');
        }
      }
    };

    // Add event listener
    document.addEventListener('keydown', handleKeyDown);

    // CLEANUP - This was missing!
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [filters, addItemDialogOpen, maintenanceDialogOpen, detailsDialogOpen, filterDrawerOpen, handleFilterChange, showNotification]);
  // CRUD OPERATIONS
  const handleAddItem = async (newItem: NewInventoryItem & { selectedImageFiles?: File[], selectedProofFile?: File[] }) => {
    setLoading(true);
    try {
      const { selectedImageFiles, selectedProofFile, ...itemData } = newItem;
      
      const formData = new FormData();
      formData.append('data', JSON.stringify(itemData));
      
      // Add compressed image files
      if (selectedImageFiles?.length) {
        selectedImageFiles.forEach(file => {
          formData.append('equipmentImages', file);
        });
      }
      
      // Add proof files
      if (selectedProofFile?.length) {
        selectedProofFile.forEach(file => {
          formData.append('equipmentDocs', file);
        });
      }
      
      console.log('Creating item with atomic endpoint...');
      console.log('Sending files:', {
        images: selectedImageFiles?.length || 0,
        documents: selectedProofFile?.length || 0
      });
      
      const response = await axiosInstance.post('/api/Inventory/create-atomic', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setInventory(prev => [...prev, response.data]);
      setAddItemDialogOpen(false);
      showNotification('Item added successfully', 'success');
    } catch (error) {
      console.error('Add item error:', error);
      showNotification('Failed to add item', 'error');
    } finally {
      setLoading(false);
    }
  };

const handleUpdateItem = async (updatedItem: InventoryUpdateData & { selectedImageFiles?: File[], selectedProofFile?: File[] }) => {
 setLoading(true);
 try {
   console.log('Updating item with atomic endpoint...');
   
   const { selectedImageFiles, selectedProofFile, newImages, newDocuments, imagesToKeep, ...itemData } = updatedItem;

   const formData = new FormData();
   formData.append('data', JSON.stringify(itemData));
   
   if (selectedImageFiles?.length) {
     selectedImageFiles.forEach(file => {
       formData.append('equipmentImages', file);
     });
   }
   
   if (selectedProofFile?.length) {
     selectedProofFile.forEach(file => {
       formData.append('equipmentDocs', file);
     });
   }

   const response = await axiosInstance.put(
     `/api/Inventory/${updatedItem._id}/update-atomic`,
     formData,
     {
       headers: {
         'Content-Type': 'multipart/form-data',
       },
     }
   );

   setInventory(prev => 
     prev.map(item => item._id === updatedItem._id ? response.data : item)
   );

   setSelectedItem(response.data);

   // COMMENT OUT THIS LINE:
   // setDetailsDialogOpen(false);

   showNotification('Item updated successfully', 'success');

 } catch (error: unknown) {
   console.error('Failed to update item:', error);
   if (error instanceof Error) {
     showNotification(error.message || 'Failed to update item', 'error');
   } else {
     showNotification('Failed to update item', 'error');
   }
 } finally {
   setLoading(false);
 }
};

  const handleDelete = async (id: string) => {
    try {
      await axiosInstance.delete(`/api/Inventory/${id}`);
  
      // Update inventory state
      setInventory((prev) => prev.filter((item) => item._id !== id));
      setSelectedItem(null);
      setDetailsDialogOpen(false);
  
      showNotification('Item deleted successfully', 'success');
    } catch (error: any) {
      console.error('Error deleting item:', error);
      showNotification(
        error.response?.data?.message || 'Failed to delete item',
        'error'
      );
    }
  };

  // MAINTENANCE AND MOVEMENT
  const handleAddMaintenance = async (itemId: string, record: Omit<MaintenanceRecord, 'id'>) => {
    setLoading(true);
    try {
      const response = await axiosInstance.post(`/api/Inventory/${itemId}/maintenance`, record);
      setInventory(prev =>
        prev.map(item => item._id === itemId ? response.data : item)
      );
      setMaintenanceDialogOpen(false);
      showNotification('Maintenance record added successfully', 'success');
    } catch (error) {
      console.error('Failed to add maintenance record:', error);
      showNotification('Failed to add maintenance record', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleMoveItem = async (itemId: string, newLocation: string) => {
    setLoading(true);
    try {
      const response = await axiosInstance.post(`/api/Inventory/${itemId}/move`, {
        newLocation,
      });
      setInventory(prev =>
        prev.map(item => item._id.toString() === itemId.toString() ? response.data : item)
      );
      showNotification('Item moved successfully', 'success');
    } catch (error) {
      console.error('Failed to move item:', error);
      showNotification('Failed to move item', 'error');
    } finally {
      setLoading(false);
    }
  };

  // IMPORT/EXPORT
  const handleExport = async () => {
    try {
      const response = await axiosInstance.get('/api/Inventory/export', {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `inventory-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      showNotification('Export completed successfully', 'success');
    } catch (error) {
      console.error('Failed to export inventory:', error);
      showNotification('Failed to export inventory', 'error');
    }
  };

  const handleImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      await axiosInstance.post('/api/Inventory/import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      fetchInventory(); // Refresh the inventory
      showNotification('Import completed successfully', 'success');
    } catch (error) {
      console.error('Failed to import inventory:', error);
      showNotification('Failed to import inventory', 'error');
    }
  };

  // FILTER HANDLERS
  const handleLocationFilter = useCallback((location: string) => {
    console.log('Filtering by location:', location);
    
    setFilters(prev => {
      // Cast to proper location type since we know it's valid from the chip click
      const locationAsType = location as any; // Type assertion for location
      const currentLocations = prev.location || [];
      
      let updatedLocations: any[];
      if (currentLocations.includes(locationAsType)) {
        // If already selected, remove it (toggle off)
        updatedLocations = currentLocations.filter(l => l !== locationAsType);
      } else {
        // If not selected, add it
        updatedLocations = [...currentLocations, locationAsType];
      }
      
      // Show notification to user
      showNotification(`Filtered by location: ${location}`, 'success');
      
      return {
        ...prev,
        location: updatedLocations.length > 0 ? updatedLocations : undefined
      };
    });
  }, [showNotification]);

  const handleShelfFilter = useCallback((itemLocation: string, shelf: string) => {
    console.log('Filtering by shelf:', shelf, 'from item at:', itemLocation);
    
    setFilters(prev => {
      // Determine which location to use for the shelf filter
      const currentLocationFilter = prev.location;
      let targetLocation: string;
      
      if (currentLocationFilter && currentLocationFilter.length === 1) {
        // If there's already a single location filter, use that
        targetLocation = currentLocationFilter[0] as string;
        console.log('Using existing location filter:', targetLocation);
      } else if (currentLocationFilter && currentLocationFilter.length > 1) {
        // If multiple locations are filtered, use the item's location
        targetLocation = itemLocation;
        console.log('Multiple locations filtered, using item location:', targetLocation);
      } else {
        // No location filter exists, use the item's location
        targetLocation = itemLocation;
        console.log('No location filter, using item location:', targetLocation);
      }
      
     // Show notification to user
      showNotification(`Filtered by ${targetLocation} - ${shelf}`, 'success');
      
      return {
        ...prev,
        location: [targetLocation as any], // Type assertion for location array
        secondLocation: shelf as any // Type assertion for secondLocation
      };
    });
  }, [showNotification]);

  const handleCategoryFilter = useCallback((category: string) => {
    console.log('Filtering by category:', category);
    
    // Cast category to Category type since we know it's valid from the chip click
    const categoryAsType = category as Category;
    
    setFilters(prev => {
      const currentCategories = prev.category || [];
      
      let updatedCategories: Category[];
      if (currentCategories.includes(categoryAsType)) {
        // If already selected, remove it (toggle off)
        updatedCategories = currentCategories.filter(c => c !== categoryAsType);
      } else {
        // If not selected, add it
        updatedCategories = [...currentCategories, categoryAsType];
      }
      
      // Show notification to user
      showNotification(`Filtered by category: ${category}`, 'success');
      
      return {
        ...prev,
        category: updatedCategories.length > 0 ? updatedCategories : undefined
      };
    });
  }, [showNotification]);

  // HANDLERS (now that dependencies are defined)
  const handleSearch = useCallback((term: string) => {
    setSearchTerm(term);
    // Don't call handleFilterChange here since search is handled separately in fetchInventory
  }, []);

  // Enhanced category selection with multiple support
  const handleCategorySelect = useCallback((category: Category) => {
    setFilters(prev => {
      const currentCategories = prev.category || [];
      const updatedCategories = currentCategories.includes(category)
        ? currentCategories.filter(c => c !== category)
        : [...currentCategories, category];

      return {
        ...prev,
        category: updatedCategories,
      };
    });
  }, []);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ 
        bgcolor: 'background.paper', 
        borderBottom: 1, 
        borderColor: 'divider',
        position: 'sticky',
        top: 0,
        zIndex: 1100,
      }}>
        <Box sx={{ px: 3, py: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => window.history.back()}>
            <ArrowLeft />
          </IconButton>
          
          <Typography variant="h6" component="h1" sx={{ flexGrow: 1 }}>
            Cooler Inventory
          </Typography>
          <Button 
            startIcon={<Settings size={18} />}
            onClick={handleCleanupOrphans}
            variant="outlined"
          >
            Cleanup ({orphanCount})
          </Button>
  
          <Stack direction="row" spacing={1.5} alignItems="center">
            {/* View Toggle */}
            <Stack direction="row" spacing={0} sx={{ 
              border: 1, 
              borderColor: 'divider', 
              borderRadius: 1,
              overflow: 'hidden'
            }}>
              <Button
                variant={viewMode === 'grid' ? 'contained' : 'outlined'}
                size="small"
                onClick={() => setViewMode('grid')}
                sx={{ 
                  borderRadius: 0,
                  minWidth: 40,
                  border: 'none',
                  '&:hover': { border: 'none' }
                }}
              >
                <Grid3X3 size={18} />
              </Button>
              <Button
                variant={viewMode === 'table' ? 'contained' : 'outlined'}
                size="small"
                onClick={() => setViewMode('table')}
                sx={{ 
                  borderRadius: 0,
                  minWidth: 40,
                  border: 'none',
                  '&:hover': { border: 'none' }
                }}
              >
                <List size={18} />
              </Button>
            </Stack>

            {/* Filter Button */}
            <Button
              variant="outlined"
              startIcon={<Filter size={18} />}
              onClick={() => setFilterDrawerOpen(true)}
              sx={{ 
                borderRadius: 'pill',
                position: 'relative'
              }}
            >
              Filters
              {Object.keys(filters).length > 0 && (
                <Chip
                  label={Object.keys(filters).length}
                  size="small"
                  color="primary"
                  sx={{ ml: 1, height: 20, minWidth: 20 }}
                />
              )}
            </Button>
  
            <input
              type="file"
              accept=".csv"
              style={{ display: 'none' }}
              id="import-file"
              onChange={handleImport}
            />
            
            <Button 
              startIcon={<UploadIcon size={18} />}
              onClick={() => document.getElementById('import-file')?.click()}
            >
              Import
            </Button>
            
            <Button 
              startIcon={<Download size={18} />}
              onClick={handleExport}
            >
              Export
            </Button>
          </Stack>
        </Box>
  
        {/* Search and Quick Filters */}
        <Box sx={{ px: 3, py: 2 }}>
          <TextField
            fullWidth
            placeholder="Find equipment..."
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={20} />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />
  
          {/* Active Filters Display */}
          {Object.keys(filters).length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                {Object.entries(filters).map(([key, value]) => {
                  if (!value || (Array.isArray(value) && value.length === 0)) return null;
                  const label = Array.isArray(value) 
                    ? `${key}: ${value.join(', ')}`
                    : `${key}: ${value}`;
                  return (
                    <Chip
                      key={key}
                      label={label}
                      onDelete={() => handleFilterChange({ [key]: undefined })}
                      size="small"
                    />
                  );
                })}
                <Chip
                  label="Clear All"
                  onDelete={() => {
                    console.log('Clear All clicked - resetting filters');
                    handleFilterChange({});
                  }}
                  color="error"
                  size="small"
                />
              </Stack>
            </Box>
          )}
  
          {/* Category Quick Filters */}
          <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 1 }}>
            {CATEGORIES.map((cat) => (
              <Chip
                key={cat}
                label={cat}
                onClick={() => handleCategorySelect(cat)}
                color={filters.category?.includes(cat) ? 'primary' : 'default'}
                sx={{ flexShrink: 0 }}
              />
            ))}
          </Box>
  
          {/* Quick Status Filters */}
          <Box sx={{ display: 'flex', gap: 1, mt: 2, overflowX: 'auto' }}>
            <Chip
              label="Needs Maintenance"
              size="small"
              color={filters.maintenanceStatus?.includes('due') ? 'error' : 'default'}
              onClick={() => handleFilterChange({
                maintenanceStatus: filters.maintenanceStatus?.includes('due')
                  ? filters.maintenanceStatus.filter(s => s !== 'due')
                  : [...(filters.maintenanceStatus || []), 'due']
              })}
            />
            <Chip
              label="Requires Power"
              size="small"
              color={filters.requiresPower ? 'warning' : 'default'}
              onClick={() => handleFilterChange({
                requiresPower: !filters.requiresPower
              })}
            />
            <Chip
              label="Insured"
              size="small"
              color={filters.isInsured ? 'success' : 'default'}
              onClick={() => handleFilterChange({
                isInsured: !filters.isInsured
              })}
            />
          </Box>
        </Box>
      </Box>
  
      {/* Main Content */}
<Box sx={{ 
  px: 3,
  pb: 6
}}>

          {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        )}
  
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
  
        {/* Conditional View Rendering */}
        {viewMode === 'grid' ? (
          <ItemGrid
            items={inventory}
            onItemClick={(item) => {
              setSelectedItem(item);
              setDetailsDialogOpen(true);
            }}
            onMaintenanceClick={(item) => {
              setSelectedItem(item);
              setMaintenanceDialogOpen(true);
            }}
            onDelete={handleDelete}
            onCategoryFilter={handleCategoryFilter}
            onLocationFilter={handleLocationFilter}
            onShelfFilter={handleShelfFilter}
          />
        ) : (
          <ItemTable
            items={inventory}
            onItemClick={(item) => {
              setSelectedItem(item);
              setDetailsDialogOpen(true);
            }}
            onMaintenanceClick={(item) => {
              setSelectedItem(item);
              setMaintenanceDialogOpen(true);
            }}
            onDelete={handleDelete}
            onCategoryFilter={handleCategoryFilter}
            onLocationFilter={handleLocationFilter}
            onShelfFilter={handleShelfFilter}
          />
        )}
      </Box>
  
      {/* Add Item FAB */}
      <Box sx={{ position: 'fixed', bottom: 100, right: 16 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => setAddItemDialogOpen(true)}
          sx={{
            borderRadius: '28px',
            padding: '12px 24px',
            fontSize: '1.1rem',
            fontWeight: 500,
            boxShadow: (theme) => theme.shadows[8],
            textTransform: 'none',
            '&:hover': {
              boxShadow: (theme) => theme.shadows[12],
              transform: 'scale(1.02)',
            },
            transition: 'all 0.2s ease-in-out',
            backgroundColor: '#F6BE00',
            minWidth: '160px',
            height: '56px',
            gap: 1,
          }}
        >
          <Plus size={24} />
          <span>Add Item</span>
        </Button>
      </Box>
  
      {/* Dialogs */}
      <FilterDrawer
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        onApplyFilters={handleFilterChange}
      />
  
      <AddItemDialog
        open={addItemDialogOpen}
        onClose={() => setAddItemDialogOpen(false)}
        onSave={handleAddItem}
      />
  
      <MaintenanceDialog
        open={maintenanceDialogOpen}
        onClose={() => setMaintenanceDialogOpen(false)}
        item={selectedItem}
        onSave={(record) => {
          if (selectedItem) {
            handleAddMaintenance(selectedItem._id.toString(), record);
          }
        }}
      />
  
      <ItemDetailsDialog
        open={detailsDialogOpen}
        onClose={() => setDetailsDialogOpen(false)}
        item={selectedItem}
        onEdit={handleUpdateItem}
        onDelete={(id) => handleDelete(id)}
        onMaintenanceClick={(item) => {
          setSelectedItem(item);
          setMaintenanceDialogOpen(true);
          setDetailsDialogOpen(false);
        }}
      />
  
      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification(prev => ({ ...prev, open: false }))}
      >
        <Alert severity={notification.type} sx={{ width: '100%' }}>
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CoolerInventoryManager;